import React, { useEffect, useMemo } from "react"
import getValue from "get-value"
import SEO from "@components/seo"
import CrossSells from "@src/components/cross-sells"
import BundleAddToCart from "@components/cart/bundle-add-to-cart"
import StampedEmbed from "@components/reviews/stamped-embed"
import { generateProductJsonLd, generateReviewJsonLd } from "@utils/seo"
import { getRegionShopUrl } from "@utils/region";
import { JsonLd } from "gatsby-plugin-next-seo"
import ImageCarousel from "@components/product/gallery"
import ContentBuilder from "@components/content-builder"
import ProductCopy from "@components/product/copy"
import Section from "@components/section"
import ServicesInfo from "@components/services-info"
import { trackEvent, trackViewItem } from "@src/services/analytics"
import { getUriFromDocument } from "@src/utils/routing"
import { getImageUrl } from "@src/utils/image"
import {
  getInitialVariant,
} from "@utils/product"

const BundleProductView = ({ pageContext, location }) => {
  const { productEntry, sanityDiscountCodes } = pageContext
  const fromList = location?.state?.fromList || null;

  const {
    bundleProductsList,
    shopifyId,
    productDescription,
    variantCopy,
    contentBuilder: contentBuilderBlocks,
    shippingConditions,
    productFeatures,
    bundleImages,
    hiddenVariants,
    hasServicesAndPoliciesSection,
    crateSize,
    isBundle,
    layout
  } = productEntry

  const servicesInfo = contentBuilderBlocks?.find(block => block?._type === "servicesPoliciesInfo") || getValue(pageContext, "servicesPoliciesInfo");
  const initialVariant = useMemo(() => {
    return getInitialVariant(productEntry.variants, location.search)
  }, [location.search])

  const initialImages = isBundle && layout !== "product" ? bundleImages : initialVariant?.variantImages;

  const filteredBundleProductsList = useMemo(() => bundleProductsList.map(product => {
    let filteredVariants = product.variants
      ?.filter((variant) => !variant.hideOnBundle || !(variant.hideOnBundle || [])
        .some(bundleProduct => bundleProduct._id == productEntry._id)
      )
      ?.filter((variant) => {
        return !hiddenVariants?.find(hiddenVariant => hiddenVariant.shopifyId === variant.shopifyId)
      })

    return {
      ...product,
      options: product.productOptions,
      variants: filteredVariants
    }
  }), [bundleProductsList])

  useEffect(() => {
    trackViewItem(bundleProductsList.map(bundleProduct => {
      const selectedVariant = bundleProduct?.variants?.[0];

      return {
        merchandise: {
          id: selectedVariant?.shopifyId,
          sku: selectedVariant?.sku,
          title: selectedVariant?.variantTitle,
          price: selectedVariant?.price,
          compareAtPrice: selectedVariant?.compareAtPrice,
          image: {
            src: getImageUrl(selectedVariant?.listingImage),
          },
          product: {
            id: bundleProduct?.shopifyId,
            title: bundleProduct?.title,
            category: bundleProduct?.productGroups?.map(group => group.title),
            list: fromList || '/collections/bundles/',
            onlineStoreUrl: getRegionShopUrl() + getUriFromDocument(bundleProduct)
          },
        },
        quantity: null,
      }
    }), fromList || '/collections/bundles/');
  }, [])

  useEffect(() => {
    if (location?.state?.fromPlpToPdp) {
      trackEvent("ctr_plp_to_pdp")
    }
  }, [location?.state?.fromPlpToPdp])


  return (
    <>
      <SEO {...{ document: productEntry }} />
      <article>
        <Section innerClassname={'grid grid-cols-12 relative'}>
          <div className='col-span-12 md:col-span-7 image-carousel-column'>
            <ImageCarousel {...{ productEntry, initialImages: initialImages, mainImageClassName: 'is-pdp', thumbnailImageClassName: 'is-pdp' }} />
          </div>
          <div className="col-span-12 md:col-span-5">
            <BundleAddToCart
              bundleProductsList={filteredBundleProductsList}
              productEntry={productEntry}
              fromList={fromList}
              mainLayout={productEntry?.layout}
              sanityDiscountCodes={sanityDiscountCodes}
            />
            {hasServicesAndPoliciesSection !== false && (
              <ServicesInfo {...servicesInfo} />
            )}
          </div>
        </Section>
        <Section>
          <ProductCopy
            {...{
              productEntry,
              productDescription,
              shippingConditions,
              productFeatures,
              variantCopy,
              crateSizes: crateSize?.sizes,
              quizLink: crateSize?.quizLink,
            }} />
        </Section>
        {contentBuilderBlocks && (
          <section className="container mx-auto mb-24">
            <div className="mx-auto text-center">
              <ContentBuilder blocks={contentBuilderBlocks} showsInPDP={true} />
            </div>
          </section>
        )}
        <CrossSells
          title="You might also like"
          intro={null}
          products={productEntry.youMightAlsoLike}
          excludeIds={[shopifyId]}
        />

        <div className="container px-8 mx-auto mb-4 sm:pt-16">
          <StampedEmbed
            type="product"
            {...{ productEntries: bundleProductsList }}
          />
        </div>
      </article>
      <JsonLd json={{ ...generateProductJsonLd(productEntry, null) }} />
      <JsonLd json={{ ...generateReviewJsonLd(productEntry, null) }} />
    </>
  )
}

export default BundleProductView
